import React, { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Instagram, Youtube, Linkedin, Facebook, User, ShoppingCart, Menu, X, Package, LogIn, LogOut } from "lucide-react"
import { useCart } from "../context/CartContext"
import { useAuth } from "../context/AuthContext"
import { supabase } from "../lib/supabase"

export function Header() {
  const navigate = useNavigate();
  const { cartItems } = useCart();
  const { user, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [accountMenuOpen, setAccountMenuOpen] = useState(false)

  const handleLogout = async () => {
    await signOut();
    setAccountMenuOpen(false);
    navigate("/");
  };

  const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  return (
    <header className="w-full">
      {/* Top Banner */}
      <div className="bg-[#5a3921] text-[#d4a84b] py-2 px-4 text-sm grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center place-items-center gap-x-4 md:gap-x-10">
        <div className="flex items-center justify-center gap-1 sm:gap-2 text-center leading-tight md:col-start-2 md:justify-self-center px-2">
          <span className="font-bold">✦</span>
          <span className="uppercase tracking-wider text-[10px] sm:text-xs md:text-sm">
            SUSTAINABLE PRACTICE • ORGANICALLY GROWN • CONTINUOUS INNOVATIONS • TRANSPARENCY
          </span>
          <span className="font-bold">✦</span>
        </div>
        <div className="hidden md:flex items-center gap-3 md:justify-self-end md:col-start-3 ml-2">
          <a 
            href="https://www.instagram.com/magicroast_qoffee/?hl=en" 
            className="w-8 h-8 rounded-full border border-white flex items-center justify-center transition-all duration-300"
            target="_blank" 
            rel="noopener noreferrer"
            title="Instagram"
          >
            <Instagram size={16} className="text-white" />
          </a>
          <a 
            href="https://www.youtube.com/channel/UCaVueE_VFiV_IgHyum0Nteg" 
            className="w-8 h-8 rounded-full border border-white flex items-center justify-center transition-all duration-300"
            target="_blank" 
            rel="noopener noreferrer"
            title="YouTube"
          >
            <Youtube size={16} className="text-white" />
          </a>
          <a 
            href="https://www.linkedin.com/company/magicroast-qoffee/about/" 
            className="w-8 h-8 rounded-full border border-white flex items-center justify-center transition-all duration-300"
            target="_blank" 
            rel="noopener noreferrer"
            title="LinkedIn"
          >
            <Linkedin size={16} className="text-white" />
          </a>
          <a 
            href="https://www.facebook.com/people/Magic-Roast-Qoffee/61572673342331/?mibextid=ZbWKwL" 
            className="w-8 h-8 rounded-full border border-white flex items-center justify-center transition-all duration-300"
            target="_blank" 
            rel="noopener noreferrer"
            title="Facebook"
          >
            <Facebook size={16} className="text-white" />
          </a>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-[#FDEBC8] py-4 px-4 md:px-8 flex items-center justify-between shadow-sm">
        {/* Logo */}
        <Link to="/" className="flex items-center group">
          <div className="w-20 h-20 rounded-full border-2 border-[#5a3921] flex items-center justify-center p-1 transition-all duration-300 group-hover:border-[#d4a84b] group-hover:scale-105 group-hover:shadow-lg">
            <div className="w-full h-full rounded-full bg-[#f5e6d3] flex items-center justify-center transition-all duration-300 group-hover:bg-[#fff8eb]">
              <img 
                src={process.env.PUBLIC_URL + "/magicroastlogo.png"} 
                alt="Magic Roast Qoffee Logo" 
                className="w-full h-full object-contain rounded-full transition-transform duration-300 group-hover:scale-110"
              />
            </div>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          <Link to="/" className="text-[#5a3921] font-medium hover:text-[#d4a84b] transition-colors">
            HOME
          </Link>
          <Link to="/about" className="text-[#5a3921] font-medium hover:text-[#d4a84b] transition-colors">
            ABOUT US
          </Link>
          <Link to="/shop" className="text-[#5a3921] font-medium hover:text-[#d4a84b] transition-colors">
            SHOP
          </Link>
          <Link to="/blogs" className="text-[#5a3921] font-medium hover:text-[#d4a84b] transition-colors">
            BLOGS
          </Link>
          <Link to="/contact" className="text-[#5a3921] font-medium hover:text-[#d4a84b] transition-colors">
            CONTACT
          </Link>
        </div>

        {/* User Actions */}
        <div className="flex items-center gap-4">
          {/* Account Dropdown */}
          <div className="relative hidden md:block">
            <button 
              className="p-2 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors"
              onClick={() => setAccountMenuOpen(!accountMenuOpen)}
              title="Profile"
            >
              <User size={20} className="text-[#5a3921]" />
            </button>
            
            {/* Dropdown Menu */}
            {accountMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50 overflow-hidden">
                {user ? (
                  <>
                    <div className="px-4 py-3 bg-[#f5e6d3]/30 border-b border-[#f5e6d3]">
                      <p className="text-sm font-bold text-[#5a3921] truncate">
                        {user.user_metadata?.full_name || user.user_metadata?.first_name || "Welcome Back"}
                      </p>
                      <p className="text-[10px] text-[#6a6a6a] truncate">{user.email}</p>
                    </div>
                    <Link 
                      to="/my-account" 
                      className="block px-4 py-3 text-[#5a3921] hover:bg-[#f5e6d3] hover:text-[#d4a84b] transition-colors font-medium flex items-center gap-2"
                      onClick={() => setAccountMenuOpen(false)}
                    >
                      <User size={16} />
                      My Account
                    </Link>
                    <Link 
                      to="/orders" 
                      className="block px-4 py-3 text-[#5a3921] hover:bg-[#f5e6d3] hover:text-[#d4a84b] transition-colors font-medium flex items-center gap-2"
                      onClick={() => setAccountMenuOpen(false)}
                    >
                      <Package size={16} />
                      Orders
                    </Link>

                    <button 
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 transition-colors font-medium flex items-center gap-2 border-t border-gray-100"
                    >
                      <LogOut size={16} />
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link 
                      to="/login" 
                      className="block px-4 py-3 text-[#5a3921] hover:bg-[#f5e6d3] hover:text-[#d4a84b] transition-colors font-medium"
                      onClick={() => setAccountMenuOpen(false)}
                    >
                      Login
                    </Link>
                    <Link 
                      to="/register" 
                      className="block px-4 py-3 text-[#5a3921] hover:bg-[#f5e6d3] hover:text-[#d4a84b] transition-colors font-medium"
                      onClick={() => setAccountMenuOpen(false)}
                    >
                      Register
                    </Link>

                  </>
                )}
              </div>
            )}
          </div>
          
          <Link 
            to="/cart"
            className="p-2 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors relative"
            title="Cart"
          >
            <ShoppingCart size={20} className="text-[#5a3921]" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#d4a84b] text-[#3d2817] text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-[#FDEBC8]">
                {cartCount}
              </span>
            )}
          </Link>
          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t shadow-lg rounded-b-2xl">
          <div className="flex flex-col p-4 gap-4">
            <Link to="/" className="text-[#5a3921] font-medium py-2 border-b">
              HOME
            </Link>
            <Link to="/about" className="text-[#5a3921] font-medium py-2 border-b">
              ABOUT US
            </Link>
            <Link to="/shop" className="text-[#5a3921] font-medium py-2 border-b">
              SHOP
            </Link>
            <Link to="/blogs" className="text-[#5a3921] font-medium py-2 border-b">
              BLOGS
            </Link>
            <Link to="/contact" className="text-[#5a3921] font-medium py-2 border-b">
              CONTACT
            </Link>
            
            {/* Mobile Account Options */}
            <div className="pt-4 border-t flex flex-col gap-2">
              {user ? (
                <>
                  <Link 
                    to="/my-account" 
                    className="flex items-center gap-2 text-[#5a3921] font-medium py-2 hover:text-[#d4a84b] transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <User size={18} />
                    My Account
                  </Link>
                  <Link 
                    to="/orders" 
                    className="flex items-center gap-2 text-[#5a3921] font-medium py-2 hover:text-[#d4a84b] transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Package size={18} />
                    Orders
                  </Link>

                  <button 
                    onClick={handleLogout}
                    className="flex items-center gap-2 text-red-600 font-medium py-2 border-t border-gray-100 mt-2 pt-2 hover:bg-red-50 transition-colors w-full text-left"
                  >
                    <LogOut size={18} />
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="flex items-center gap-2 text-[#5a3921] font-medium py-2 hover:text-[#d4a84b] transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <LogIn size={18} />
                    Login
                  </Link>
                  <Link 
                    to="/register" 
                    className="flex items-center gap-2 text-[#5a3921] font-medium py-2 hover:text-[#d4a84b] transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <User size={18} />
                    Register
                  </Link>

                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
